// News item — canonical shape for the Intel hot-zone popup (W2-B) and the
// Major News side panel (W2-C).
//
// Wave 2 ships this against a static JSON fixture so the UX surface lands
// before the Inference orchestrator's real news connector. The shape is
// deliberately small + connector-agnostic: any future ingester (RSS, GDELT
// document layer, ACLED narrative, ISW reports, etc.) hands the UI rows
// matching this contract.
//
// Iron Rule #11: this lives in `intel-feed-client` so the Vendor and Agency
// maps inherit the same model when they overlay intel signals.
// Iron Rule #4 / #12: no LLM call lives in this file. `credibility_score`
// is computed by the deterministic stub in `./credibility.ts` until the
// inference gateway publishes a per-source-credibility artifact.

import { z } from 'zod';

/**
 * 2-letter region code — matches the UN M49 broad-region buckets we use
 * everywhere else (NA, SA, EU, AF, AS, OC, ME for Middle East, AN for
 * Antarctica, GL for global/unknown). Lower-cased ok-ish for forward compat;
 * the UI normalizes to upper-case on render.
 */
export const NewsRegion = z
  .string()
  .min(2)
  .max(2)
  .regex(/^[A-Za-z]{2}$/);
export type NewsRegion = z.infer<typeof NewsRegion>;

/**
 * Source descriptor. `url` is the *publisher* root (e.g. `https://reuters.com`)
 * — useful for favicon resolution. The per-article URL lives on `NewsItem.url`.
 * `name` is the human-facing label that lands on the card chrome.
 */
export const NewsSource = z
  .object({
    name: z.string().min(1).max(64),
    url: z.string().url(),
  })
  .strict();
export type NewsSource = z.infer<typeof NewsSource>;

/**
 * NewsItem — the row.
 *
 *   * `linked_signal_ids` — soft join into the `IntelSignal` stream. The
 *     fixture wires a handful so hover-on-a-marker → "+N RELATED" chip lights
 *     up. Real ingester will populate via `services/intel-fusion`.
 *   * `severity` — 0..100, mirrors `IntelSignal.severity` so the side-panel
 *     dot uses the same heat ramp as the map.
 *   * `credibility_score` — 0..100. Deterministic stub today; replaced by the
 *     inference gateway's source-credibility artifact later.
 */
export const NewsItem = z
  .object({
    id: z.string().min(1).max(64),
    title: z.string().min(1).max(280),
    summary: z.string().min(1).max(600),
    url: z.string().url(),
    source: NewsSource,
    published_at: z.string().min(8), // ISO timestamp
    credibility_score: z.number().int().min(0).max(100),
    region: NewsRegion,
    linked_signal_ids: z.array(z.string()).max(64),
    severity: z.number().int().min(0).max(100),
  })
  .strict();
export type NewsItem = z.infer<typeof NewsItem>;

/** Wire response envelope for the eventual `/v1/news/feed` endpoint. */
export const NewsFeedResponse = z
  .object({
    items: z.array(NewsItem),
  })
  .strict();
export type NewsFeedResponse = z.infer<typeof NewsFeedResponse>;
